import { useReactiveVar } from "@apollo/client";
import { CalendarToday } from "@mui/icons-material";
import { CircularProgress, Typography } from "@mui/material";
import React from "react";

import { useAppState } from "../../hooks/useAppState";
import { slideViewsLoadingVar } from "../../hooks/useCourseProgress";
import { useProgress } from "../../hooks/useProgress";
import { useProgressModal } from "../../hooks/useProgressModal";
import { getTimeDescription } from "../../util/time";
import {
  CourseExpiration,
  DetailsLink,
  ProgressBar,
  ProgressContainer,
  ProgressHeader,
  ProgressHorizontalLine,
  ProgressText,
  ProgressTextWrapper,
} from "./Progress.elements";

export function Progress(): React.ReactElement | null {
  const { progress, expiration } = useProgress();
  const { openModal: openModalWith } = useProgressModal();
  const {
    appState: { courseId, instanceId },
    course,
  } = useAppState();
  const isRecalculatingProgress = useReactiveVar(slideViewsLoadingVar);

  if (!progress) {
    return null;
  }

  const { percentSlides, hoursRemaining } = progress;

  const percentCompleted = Math.ceil(percentSlides);

  const openModal = () => {
    if (courseId && instanceId && course?.id) {
      openModalWith({ courseId, courseUuid: course.id, instanceId });
    }
  };

  const handleKeyPress = (event: React.KeyboardEvent) => {
    const actionMap = {
      Enter: openModal,
    } as const;

    type Key = keyof typeof actionMap;
    const key = event.key as Key;

    return actionMap[key]?.();
  };

  return (
    <ProgressContainer role="complementary" aria-label="Course progress">
      <ProgressHeader>
        <div className="progress-percent">
          <Typography variant="h5" component="h2" aria-label={`${percentCompleted}% completed`}>
            {`Progress: ${percentCompleted}%`}
          </Typography>
        </div>
        <DetailsLink
          tabIndex={2}
          onClick={openModal}
          onKeyPress={handleKeyPress}
          aria-label="View progress details"
          id="view_progress_link"
        >
          View Course Progress
        </DetailsLink>
      </ProgressHeader>
      <ProgressTextWrapper>
        <ProgressText aria-label={`approximately ${getTimeDescription(hoursRemaining, "hours")} remaining`}>
          {`~${getTimeDescription(hoursRemaining, "hours", true)} remaining`}
        </ProgressText>
        {isRecalculatingProgress && (
          <CircularProgress
            size={12}
            thickness={4}
            sx={{ ml: 1, color: "text.secondary" }}
            aria-label="Updating progress..."
          />
        )}
      </ProgressTextWrapper>
      <ProgressBar variant="determinate" value={percentSlides} />
      <CourseExpiration>
        <CalendarToday fontSize="small" />
        <div className="expiration">
          <div>{expiration?.expirationText}</div>
          <div>{expiration?.expirationDate}</div>
        </div>
      </CourseExpiration>
      <ProgressHorizontalLine />
    </ProgressContainer>
  );
}
